import React, { useState, useEffect } from "react";
import producticon1 from "../../../Assets/product/product-icon-1.png";
import producticon2 from "../../../Assets/product/product-icon-2.png";
import producticon3 from "../../../Assets/product/product-icon-3.png";
import producticon4 from "../../../Assets/product/product-icon-4.png";
import producticon5 from "../../../Assets/product/product-icon-5.png";
import { IoStarSharp } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import API from "../../../Utils/api";

// Product Card Skeleton
const ProductSkeleton = () => (
  <div className="bg-white shadow-md rounded-2xl p-4 flex flex-col items-center border animate-pulse">
    <div className="w-full h-[150px] bg-gray-200 rounded-xl" />
    <div className="flex gap-1 mt-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="w-4 h-4 bg-gray-200 rounded-full" />
      ))}
    </div>
    <div className="h-5 bg-gray-200 rounded w-3/4 mt-3" />
    <div className="h-3 bg-gray-200 rounded w-2/3 mt-2" />
    <div className="h-3 bg-gray-200 rounded w-2/3 mt-2" />
    <div className="h-3 bg-gray-200 rounded w-1/2 mt-2" />
    <div className="h-8 bg-gray-200 rounded-2xl w-28 mt-3" />
  </div>
);

// Category bottom static icons
const categoryIcons = [
  {
    img: producticon1,
    name: "Micronutrients",
    description:
      "Essential micronutrients for stronger growth and higher crop yield",
  },
  {
    img: producticon2,
    name: "Water Soluble Fertilizers",
    description: "Natural protection ensuring healthier crops and pest control",
  },
  {
    img: producticon3,
    name: "Bio-Pesticides & Larvicides",
    description:
      "Fast dissolving nutrients for efficient plant absorption and growth",
  },
  {
    img: producticon4,
    name: "Plant Growth Regulators",
    description:
      "Balanced growth regulation for healthier plants and higher yields",
  },
  {
    img: producticon5,
    name: "Bio Fertilisers & Organic Fertilisers",
    description:
      "Natural nutrients improving soil fertility and sustainable crop growth",
  },
];

const Products = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchProducts = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await API.get("/product/all");
      const data = Array.isArray(res.data.data) ? res.data.data : [];
      setProducts(data);
    } catch (err) {
      console.error("Error fetching products:", err);
      setError("Failed to load products.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  return (
    <div className="w-full px-4 sm:px-6 md:px-10 py-4 sm:py-8 bg-yellow-50/30">
      {/* Header */}
      <div className="flex flex-col items-center justify-center text-center">
        <p className="text-[#92AD36] text-sm font-semibold tracking-wide">
          EXPLORE PRODUCTS
        </p>
        <h2 className="text-2xl md:text-4xl font-serif text-[#000000] mt-3">
          Featured Products
        </h2>
        <p className="text-[#848484] mt-3 max-w-2xl leading-relaxed text-sm md:text-base">
          Eco-friendly fertilizers are the nutrients that do not harm the
          environment, whether in their production or application.
        </p>
      </div>

      {/* Error State */}
      {error && !loading && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <span className="text-5xl mb-4">😕</span>
          <p className="text-gray-600 font-medium">{error}</p>
          <button
            onClick={fetchProducts}
            className="mt-4 px-6 py-2 bg-[#92AD36] text-white rounded-2xl text-sm hover:bg-[#7a9230] transition"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && products.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <span className="text-5xl mb-4">📦</span>
          <p className="text-gray-600 font-medium">No products available yet</p>
          <p className="text-gray-400 text-sm mt-1">Check back soon</p>
        </div>
      )}

      {/* Products Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mt-10">
        {loading
          ? Array.from({ length: 8 }).map((_, i) => <ProductSkeleton key={i} />)
          : products.map((item) => (
              <div
                key={item._id}
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                  navigate(`/products/${item._id}`);
                }}
                className="bg-white relative shadow-md rounded-2xl p-4 flex flex-col items-center border hover:border-[#92AD36] mb-5 transition hover:scale-105 duration-300 hover:shadow-xl hover:backdrop-blur-sm cursor-pointer"
              >
                <img
                  src={item.images?.[0]}
                  alt={item.name}
                  className="w-full h-[150px] object-contain"
                />
                <span
                  className={`absolute top-1 right-1 px-3 py-1 text-xs rounded-2xl text-white ${item.stock > 0 ? "bg-[#407B00]" : "bg-red-500"}`}
                >
                  {item.stock > 0 ? "In Stock" : "Out of Stock"}
                </span>

                {/* Stars */}
                <div className="flex text-yellow-400 mt-3">
                  {[...Array(5)].map((_, i) => (
                    <IoStarSharp
                      key={i}
                      size={20}
                      className={
                        i < item.rating ? "text-yellow-400" : "text-gray-200"
                      }
                    />
                  ))}
                </div>

                <h3 className="text-xl font-semibold text-[#425340] mt-2 text-center">
                  {item.name}
                </h3>
                <p className="text-sm mt-2 text-[#425340]">
                  <span>Dosage: </span>
                  {item.dosage || "—"}
                </p>
                <p className="text-sm mt-2 text-[#425340]">
                  <span>Pack Size: </span>
                  {item.packSizes?.join(", ") || "—"}
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.scrollTo({ top: 0, behavior: "smooth" });
                    navigate(`/products/${item._id}`);
                  }}
                  className="px-6 py-2 text-sm mt-2 bg-white text-[#407B00] rounded-2xl border border-[#92AD36] hover:bg-[#92AD36] duration-300 hover:text-white"
                >
                  Enquiry Now
                </button>
              </div>
            ))}
      </div>
      <div className="pb-5">
        <button
          onClick={() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
            navigate("/products");
          }}
          className="absolute  left-1/2 -translate-x-1/2 bg-[#92AD36] text-white hover:bg-white hover:text-[#92AD36] border border-[#92AD36] duration-300 px-8 py-2 text-sm rounded-2xl"
        >
          View All
        </button>
      </div>
      {/* Category Icons Bottom Section */}
      <div className="relative grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-10">
        {categoryIcons.map((item, index) => (
          <div
            key={index}
            className="bg-white shadow-md rounded-2xl p-3 flex flex-col items-center border hover:border-[#92AD36] mb-5 transition hover:scale-105 duration-300 hover:shadow-xl hover:backdrop-blur-sm"
          >
            <img
              src={item.img}
              alt=""
              className="h-[30px] w-full object-contain mt-4"
            />
            <p className="text-[#132728] font-semibold text-base mt-3 text-center">
              {item.name}
            </p>
            <p className="text-[#585F5F] text-xs mt-3 text-center">
              {item.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Products;
